import { Box, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import DateRangePicker from "src/components/common/DateRangePicker";
import { DateTime } from "luxon";
import {
    buttonItem,
    containerStyle,
    datePickerItem,
    filterItem,
    statusFilter,
    searchItem,
    typeFilter,
} from "./PreBidsStyle";
import MultiDropDown from "src/components/common/MultiDropDown";
import { BsPlusLg } from "react-icons/bs";
import ActionButton from "src/components/common/ActionButton";
import { OrderTypes } from "src/enums/sortingsEnum";
import { calcPaginationProps } from "src/utils/helperFunctions";
import { PaginationPropsType } from "src/types/paginationTypes";
import {
    BackorderData,
    OrderPropsType,
    SelectedBid,
} from "src/types/allBidsPageTypes";
import { payloadType } from "src/types/dataFetchingTypes";
import useData from "src/hooks/useApi";
import useMessage from "src/hooks/useMessage";
import TablePagination from "src/components/table/TablePagination";
import SearchField from "src/components/common/SearchField";
import {
    BackorderTypeEnum,
    PreAuctionEnum,
    ProgressStatusEnum,
    UserStatusEnum,
} from "../../enums/allBidsPageEnums";
import {
    backorderStatusSelections,
    backorderTypeSelections,
} from "../../data/bidsStatusFilter";
import useNotification from "src/hooks/useNotification";
import { isUserCompleted } from "src/utils/jwtFunctions";
import BackorderModel from "src/components/allBids/BackorderModel";
import BackorderTable from "src/components/allBids/PreBidsPageTable";
import DeletePreBidModel from "src/components/allBids/DeleteModel";
import { BackorderPopupModeEnum } from "src/enums/allBidsPageEnums";
import PreBackorderModel from "src/components/allBids/PreBackorderModel";
import EditBackorderModel from "src/components/allBids/EditBackorderModel";
import EditPreBackorderModel from "src/components/allBids/EditPreBackorderModel";

const PreBidsPage = ({ currentTab }: { currentTab: string }) => {
    // Hooks

    const { showError } = useMessage();
    const { fetchData } = useData();

    const api = useData();
    const lastBalanceUpdateTime = useNotification();

    // Filters and searches
    const [endDate, setEndDate] = useState<DateTime>();
    const [startDate, setStartDate] = useState<DateTime>();
    const [selectedStatus, setSelectedStatus] = useState<string[]>([]);
    const [selectedBackorderType, setSelectedBackorderType] = useState<
        BackorderTypeEnum[]
    >([]);
    const [searchValue, setSearchValue] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);

    // common
    const [paginationProps, setPaginationProps] = useState<PaginationPropsType>(
        {
            pages: 0,
            rowsPerPage: 10,
            currentPage: 1,
        },
    );
    const [tableData, setTableData] = useState<BackorderData[]>([]);
    const [orderProps, setOrderProps] = useState<OrderPropsType>({
        order: OrderTypes.ASC,
        orderBy: "dropTime",
    });

    // Popup states

    const [openBackorder, setOpenBackorder] = useState(false);
    const [editBackorder, setEditBackorder] = useState<boolean>(false);

    const [openPreBackorder, setOpenPreBackorder] = useState(false);
    const [editPreBackorder, setEditPreBackorder] = useState<boolean>(false);

    const [deletePopup, setDeletePopup] = useState<boolean>(false);

    const [selectedBid, setSelectedBid] = useState<SelectedBid>();

    const [availBids, setAvailBids] = useState<number>(0);
    const [reloadData, setReloadData] = useState<number>(Date.now() / 1000);

    const getData = async (isReset: boolean = false) => {
        try {
            setLoading(true);
            const payload: payloadType = {
                page: paginationProps.currentPage,
                size: paginationProps.rowsPerPage,
                filterAnd: [],
                filterOr: [],
                sort: {
                    key: orderProps.orderBy,
                    order: orderProps.order.toUpperCase(),
                },
            };
            if (isReset) {
                payload.page = 1;
                setPaginationProps((paginationProps) => ({
                    ...paginationProps,
                    currentPage: 1,
                }));
            }

            if (selectedStatus.length > 0) {
                const statusSet: string[] = [];

                if (selectedStatus.includes(PreAuctionEnum.PAYMENT)) {
                    statusSet.push(ProgressStatusEnum.SUCCESS);
                }

                if (selectedStatus.includes(PreAuctionEnum.INPROGRESS)) {
                    statusSet.push(ProgressStatusEnum.WAITING);
                    statusSet.push(ProgressStatusEnum.INPROGRESS);
                }

                if (selectedStatus.includes(UserStatusEnum.SUBMISSIONPEND)) {
                    statusSet.push(ProgressStatusEnum.PENDING);
                }

                if (selectedStatus.includes(UserStatusEnum.RESULT_PENDING)) {
                    statusSet.push(ProgressStatusEnum.RESULT_PENDING);
                }

                payload.filterAnd!.push({
                    key: "status",
                    operation: "IN",
                    value: [...statusSet],
                });
            }

            if (selectedBackorderType.length === 1) {
                payload.filterAnd!.push({
                    key: "isPreBackorder",
                    operation: "=",
                    value: selectedBackorderType.includes(
                        BackorderTypeEnum.PREBACKORDER,
                    ),
                });
            }

            if (searchValue.trim().length > 0 && payload.filterAnd) {
                payload.filterAnd.push({
                    key: "domainName",
                    operation: "LIKE",
                    value: [`${searchValue.trim()}%`],
                });
            }

            if (startDate && payload.filterAnd) {
                payload.filterAnd.push({
                    key: "dropTime",
                    operation: ">",
                    value: Math.round(startDate.toSeconds()),
                });
            }

            if (endDate && payload.filterAnd) {
                payload.filterAnd.push({
                    key: "dropTime",
                    operation: "<",
                    value: Math.round(endDate.toSeconds()),
                });
            }

            const response: any = await api?.postData(
                "/bid/getBackorders",
                payload,
            );

            if (response.data) {
                setTableData(response.data);
            } else {
                setTableData([]);
            }
            setPaginationProps((paginationProps) =>
                calcPaginationProps(paginationProps, response.count),
            );
        } catch (error: any) {
            setTableData([]);
            showError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const getDomainsCount = async () => {
        try {
            const response: any = await fetchData("/bid/availableBidCount");
            if (response <= 0) {
                setAvailBids(0);
            }
            setAvailBids(response);
        } catch (error: any) {
            showError(error.message);
        }
    };

    const updateBidAmount = (selectedBid: SelectedBid) => {
        if (!isUserCompleted()) {
            showError("Please complete the profile");
            return;
        }

        setSelectedBid(selectedBid);

        if (selectedBid.isPreBackorder) {
            setEditPreBackorder(true);
        } else {
            setEditBackorder(true);
        }
    };

    const bidDeleteAction = (selectedBid: SelectedBid) => {
        if (!isUserCompleted()) {
            showError("Please complete the profile");
        } else {
            setSelectedBid(selectedBid);
            setDeletePopup(true);
        }
    };

    const handleChangeSearch = (e: any) => {
        setSearchValue(e.target.value.trim().toLowerCase());
    };

    const openAddDialogBox = (mode: BackorderPopupModeEnum) => {
        if (!isUserCompleted()) {
            showError("Please complete the profile");
        } else if (availBids <= 0) {
            showError("You have already exceeded the daily domain limit");
        } else {
            if (mode === BackorderPopupModeEnum.BACKORDER) {
                setOpenBackorder(true);
            } else if (mode === BackorderPopupModeEnum.PRE_BACKORDER) {
                setOpenPreBackorder(true);
            }
        }
    };

    useEffect(() => {
        getData();
    }, [
        paginationProps.currentPage,
        orderProps.orderBy,
        orderProps.order,
        selectedStatus,
        selectedBackorderType,
        endDate,
        startDate,
        paginationProps.rowsPerPage,
        currentTab,
        lastBalanceUpdateTime,
        reloadData,
    ]);

    // To Clear the Filter on Search Field Clear
    useEffect(() => {
        if (!searchValue) getData(true);
    }, [searchValue]);

    useEffect(() => {
        if (tableData.length <= 0 && paginationProps.currentPage !== 1) {
            setPaginationProps((paginationProps) => ({
                ...paginationProps,
                currentPage: 1,
            }));
        }
    }, [tableData]);

    useEffect(() => {
        getDomainsCount();
    }, []);

    return (
        <Box alignItems="center" sx={containerStyle}>
            {selectedBid && editBackorder && (
                <EditBackorderModel
                    open={editBackorder}
                    domainName={selectedBid.domainName}
                    priority={selectedBid.priority}
                    setOpen={setEditBackorder}
                    clearSelectedBid={() => setSelectedBid(undefined)}
                    setReloadData={setReloadData}
                />
            )}

            {selectedBid && editPreBackorder && (
                <EditPreBackorderModel
                    open={editPreBackorder}
                    domainName={selectedBid.domainName}
                    bidAmount={selectedBid.bidAmount}
                    setOpen={setEditPreBackorder}
                    clearSelectedBid={() => setSelectedBid(undefined)}
                    setReloadData={setReloadData}
                />
            )}

            {selectedBid && (
                <DeletePreBidModel
                    open={deletePopup}
                    bidId={selectedBid.bidId}
                    domainName={selectedBid.domainName}
                    setOpen={setDeletePopup}
                    clearSelectedBid={() => setSelectedBid(undefined)}
                    setReloadData={setReloadData}
                />
            )}

            <BackorderModel
                openBackorder={openBackorder}
                maxPossibleOrders={availBids}
                setOpenBackorder={setOpenBackorder}
                setReloadData={setReloadData}
            />

            <PreBackorderModel
                openPreBackorder={openPreBackorder}
                setOpenPreBackorder={setOpenPreBackorder}
                setReloadData={setReloadData}
            />

            <Grid
                container
                justifyContent={"space-between"}
                paddingBottom={containerStyle.paddingTop}
                rowGap={3}
            >
                <Grid item container rowGap={2} columnGap={1} {...filterItem}>
                    <Grid item {...searchItem}>
                        <SearchField
                            iconPosition="start"
                            onChange={handleChangeSearch}
                            placeholder="Search Domain"
                            value={searchValue}
                            id="Search-button-all-bids"
                            keyPressTrigger={(e) => {
                                if (e.code === "Enter" || e.key === "Enter") {
                                    getData(true);
                                }
                            }}
                        />
                    </Grid>
                    <Grid item {...statusFilter}>
                        <MultiDropDown
                            id="all-bids-status-dropdown"
                            label="Progress Status"
                            checkIcon={false}
                            setSelectedItems={setSelectedStatus}
                            selectedItems={selectedStatus}
                            data={backorderStatusSelections}
                            containerStyles={{
                                width: "100%",
                            }}
                        />
                    </Grid>
                    <Grid item {...typeFilter}>
                        <MultiDropDown
                            id="all-bids-pb-dropdown"
                            label="Backorder Type"
                            checkIcon={false}
                            setSelectedItems={setSelectedBackorderType}
                            selectedItems={selectedBackorderType}
                            data={backorderTypeSelections}
                            containerStyles={{
                                width: "100%",
                            }}
                        />
                    </Grid>
                    <Grid item {...datePickerItem}>
                        <DateRangePicker
                            startDate={startDate}
                            endDate={endDate}
                            setEndDate={setEndDate}
                            setStartDate={setStartDate}
                            startDateLabel={"Drop Time"}
                            endDateLabel={"Drop Time"}
                        />
                    </Grid>
                </Grid>
                <Grid item container {...buttonItem} columnGap={1} rowGap={1}>
                    <Grid item>
                        <ActionButton
                            icon={<BsPlusLg />}
                            iconPosition="start"
                            label="Pre-Backorder"
                            triggerFunction={() =>
                                openAddDialogBox(
                                    BackorderPopupModeEnum.PRE_BACKORDER,
                                )
                            }
                            style={{
                                width: { xs: "100%", sm: "160px" },
                                height: "40px",
                            }}
                            isSecondary={true}
                        />
                    </Grid>
                    <Grid item>
                        <ActionButton
                            icon={<BsPlusLg />}
                            iconPosition="start"
                            label="Backorder"
                            triggerFunction={() =>
                                openAddDialogBox(
                                    BackorderPopupModeEnum.BACKORDER,
                                )
                            }
                            style={{
                                width: { xs: "100%", sm: "130px" },
                                height: "40px",
                            }}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <BackorderTable
                backorderData={tableData}
                orderProps={orderProps}
                loading={loading}
                setOrderProps={setOrderProps}
                updateBidAmount={updateBidAmount}
                deleteBid={bidDeleteAction}
            />
            <TablePagination
                paginationProps={paginationProps}
                setPaginationProps={setPaginationProps}
            />
        </Box>
    );
};

export default PreBidsPage;
